export enum ConnectionStatus {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  RECONNECTING = "reconnecting",
  ERROR = "error",
}

type MessageCallback = (data: any) => void;
type StatusCallback = (status: ConnectionStatus) => void;

export type MarketType = "spot" | "eco" | "futures";

class WebSocketManager {
  private static instance: WebSocketManager;
  private connections: Map<string, WebSocket> = new Map();
  private connectionStatus: Map<string, ConnectionStatus> = new Map();
  private subscriptions: Map<string, Map<string, Set<MessageCallback>>> =
    new Map();
  private statusListeners: Map<string, Set<StatusCallback>> = new Map();
  private reconnectTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private messageQueues: Map<string, any[]> = new Map();
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private debug = import.meta.env.NODE_ENV !== "production";

  public static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  public connect(url: string, connectionId = "default"): void {
    if (
      this.connections.has(connectionId) &&
      (this.connectionStatus.get(connectionId) === ConnectionStatus.CONNECTED ||
        this.connectionStatus.get(connectionId) === ConnectionStatus.CONNECTING)
    ) {
      return;
    }

    if (!this.messageQueues.has(connectionId)) {
      this.messageQueues.set(connectionId, []);
    }

    this.connectionStatus.set(connectionId, ConnectionStatus.CONNECTING);
    this.notifyStatusListeners(connectionId);

    try {
      const getCookie = (name: string) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop()?.split(';').shift();
        return null;
      };
      
      const accessToken = getCookie('accessToken');
      
      let authUrl = url;
      if (accessToken) {
        const separator = url.includes('?') ? '&' : '?';
        authUrl = `${url}${separator}token=${accessToken}`;
      }
      
      const ws = new WebSocket(authUrl);

      ws.onopen = () => this.handleOpen(connectionId);
      ws.onmessage = (event) => this.handleMessage(event, connectionId);
      ws.onclose = () => this.handleClose(connectionId, url);
      ws.onerror = (error) => this.handleError(error, connectionId);

      this.connections.set(connectionId, ws);
    } catch (error) {
      console.error(
        `Error creating WebSocket connection for ${connectionId}:`,
        error
      );
      this.handleError(new Event("error"), connectionId);
    }
  }

  private handleOpen(connectionId: string): void {
    this.connectionStatus.set(connectionId, ConnectionStatus.CONNECTED);
    this.reconnectAttempts.set(connectionId, 0);
    this.notifyStatusListeners(connectionId);
    this.processMessageQueue(connectionId);
  }

  private processMessageQueue(connectionId: string): void {
    if (!this.messageQueues.has(connectionId)) return;

    const queue = this.messageQueues.get(connectionId)!;
    if (queue.length === 0) return;

    while (queue.length > 0) {
      const message = queue.shift()!;
      this.sendMessageImmediate(message, connectionId);
    }
  }

  private handleMessage(event: MessageEvent, connectionId: string): void {
    try {
      const data = JSON.parse(event.data);
      let streamKey = data.stream || "default";
      
      if (data.method === "reply" && data.payload?.id) {
        streamKey = `ticket-${data.payload.id}`;
      } else if (data.method === "update" && data.payload?.id) {
        streamKey = `ticket-${data.payload.id}`;
      }

      if (this.subscriptions.has(connectionId)) {
        const connectionSubscriptions = this.subscriptions.get(connectionId)!;
        if (connectionSubscriptions.has(streamKey)) {
          const callbacks = connectionSubscriptions.get(streamKey)!;
          callbacks.forEach((callback) => {
            try {
              callback(data.data || data);
            } catch (error) {
              console.error(`Error in callback for ${streamKey}:`, error);
            }
          });
        }
      }
    } catch (error) {
      console.error(
        `Error parsing WebSocket message for ${connectionId}:`,
        error
      );
    }
  }

  private handleClose(connectionId: string, url: string): void {
    this.connections.delete(connectionId);
    this.connectionStatus.set(connectionId, ConnectionStatus.DISCONNECTED);
    this.notifyStatusListeners(connectionId);
    this.reconnect(connectionId, url);
  }

  private handleError(error: Event, connectionId: string): void {
    console.error(`WebSocket error for ${connectionId}:`, error);
  }

  private reconnect(connectionId: string, url: string): void {
    if (this.reconnectTimeouts.has(connectionId)) {
      clearTimeout(this.reconnectTimeouts.get(connectionId)!);
    }

    const attempts = this.reconnectAttempts.get(connectionId) || 0;

    if (attempts >= this.maxReconnectAttempts) {
      console.error(
        `Maximum reconnect attempts (${this.maxReconnectAttempts}) reached for ${connectionId}`
      );
      return;
    }

    this.connectionStatus.set(connectionId, ConnectionStatus.RECONNECTING);
    this.notifyStatusListeners(connectionId);

    const delay = Math.min(this.reconnectDelay * Math.pow(2, attempts), 30000);

    const timeout = setTimeout(() => {
      this.reconnectAttempts.set(connectionId, attempts + 1);
      this.connect(url, connectionId);
    }, delay);

    this.reconnectTimeouts.set(connectionId, timeout);
  }

  public subscribe(
    streamKey: string,
    callback: MessageCallback,
    connectionId = "default"
  ): void {
    if (!this.subscriptions.has(connectionId)) {
      this.subscriptions.set(connectionId, new Map());
    }

    const connectionSubscriptions = this.subscriptions.get(connectionId)!;
    if (!connectionSubscriptions.has(streamKey)) {
      connectionSubscriptions.set(streamKey, new Set());
    }

    connectionSubscriptions.get(streamKey)!.add(callback);
  }

  public unsubscribe(
    streamKey: string,
    callback: MessageCallback,
    connectionId = "default"
  ): void {
    if (this.subscriptions.has(connectionId)) {
      const connectionSubscriptions = this.subscriptions.get(connectionId)!;
      if (connectionSubscriptions.has(streamKey)) {
        const callbacks = connectionSubscriptions.get(streamKey)!;
        callbacks.delete(callback);

        if (callbacks.size === 0) {
          connectionSubscriptions.delete(streamKey);
        }
      }
    }
  }

  public sendMessage(message: any, connectionId = "default"): void {
    if (
      this.connections.has(connectionId) &&
      this.connectionStatus.get(connectionId) === ConnectionStatus.CONNECTED
    ) {
      this.sendMessageImmediate(message, connectionId);
    } else {
      if (!this.messageQueues.has(connectionId)) {
        this.messageQueues.set(connectionId, []);
      }

      this.messageQueues.get(connectionId)!.push(message);
    }
  }

  private sendMessageImmediate(message: any, connectionId: string): void {
    const connection = this.connections.get(connectionId);
    if (connection && connection.readyState === WebSocket.OPEN) {
      const messageStr = JSON.stringify(message);
      connection.send(messageStr);
    } else {
      console.error(
        `Cannot send message: WebSocket for ${connectionId} is not open`
      );
    }
  }

  public addStatusListener(
    callback: StatusCallback,
    connectionId = "default"
  ): void {
    if (!this.statusListeners.has(connectionId)) {
      this.statusListeners.set(connectionId, new Set());
    }

    this.statusListeners.get(connectionId)!.add(callback);

    const status =
      this.connectionStatus.get(connectionId) || ConnectionStatus.DISCONNECTED;
    callback(status);
  }

  public removeStatusListener(
    callback: StatusCallback,
    connectionId = "default"
  ): void {
    if (this.statusListeners.has(connectionId)) {
      this.statusListeners.get(connectionId)!.delete(callback);
    }
  }

  private notifyStatusListeners(connectionId: string): void {
    const status =
      this.connectionStatus.get(connectionId) || ConnectionStatus.DISCONNECTED;
    if (this.statusListeners.has(connectionId)) {
      this.statusListeners.get(connectionId)!.forEach((callback) => {
        try {
          callback(status);
        } catch (error) {
          console.error(`Error in status listener for ${connectionId}:`, error);
        }
      });
    }
  }

  public getStatus(connectionId = "default"): ConnectionStatus {
    return (
      this.connectionStatus.get(connectionId) || ConnectionStatus.DISCONNECTED
    );
  }

  public close(connectionId = "default"): void {
    const timeout = this.reconnectTimeouts.get(connectionId);
    if (timeout) {
      clearTimeout(timeout);
      this.reconnectTimeouts.delete(connectionId);
    }
    
    const connection = this.connections.get(connectionId);
    if (connection) {
      connection.close();
      this.connections.delete(connectionId);
      this.connectionStatus.set(connectionId, ConnectionStatus.DISCONNECTED);
      this.notifyStatusListeners(connectionId);
      
      this.subscriptions.delete(connectionId);
      this.messageQueues.delete(connectionId);
      this.reconnectAttempts.delete(connectionId);
    }
  }

  public closeAll(): void {
    this.connections.forEach((connection, connectionId) => {
      this.close(connectionId);
    });
  }
}

export const wsManager = WebSocketManager.getInstance();
